import { dependencies } from '@pn/core/dependencies';
import type { DataItemId } from '@pn/core/domain/data';
import type { Layer } from '@pn/core/domain/layer';
import { getSourceId, type WorkspaceItem } from '@pn/core/domain/workspace';
import { useWorkspaceStorage } from '@pn/core/storage';
import { dispositions } from '@pn/core/storage/workspace/pnWorkspaceItems/dispositions';
import { dispositions_historical } from '@pn/core/storage/workspace/pnWorkspaceItems/dispositions_historical';
import { isNil } from 'lodash-es';
import mapboxgl from 'mapbox-gl';
import React from 'react';

const itemsToHighlight: WorkspaceItem[] = [
  dispositions,
  dispositions_historical,
];

const itemIdsToHighlight: string[] = itemsToHighlight.map((item) => item.id);

export const layerIdsToHighlight: Layer['id'][] = itemsToHighlight.flatMap(
  (item) => item.map.layers.map((layer) => layer.id)
);

export function useLayerHighlighting(tableOnly = false, hideCrosshair = false) {
  const { map } = dependencies;

  const { workspaceItems } = useWorkspaceStorage();

  const shouldSkip = tableOnly || hideCrosshair;

  React.useEffect(() => {
    if (shouldSkip) return;

    const mouseMoveHandlers: Record<
      Layer['id'],
      (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
    > = {};
    const mouseLeaveHandlers: Record<Layer['id'], () => void> = {};

    const highlighted: Record<Layer['id'], DataItemId> = {};
    const sourceLayers: Record<Layer['id'], Layer['sourceLayer']> = {};

    for (const item of Object.values(workspaceItems)) {
      if (item.id === '_background' || item.id === 'grids') continue;
      if (!itemIdsToHighlight.includes(item.id)) continue;

      for (const layer of item.map.layers) {
        sourceLayers[layer.id] = layer.sourceLayer;

        mouseMoveHandlers[layer.id] = (e) => {
          const featureId: DataItemId = e.features[0].id;

          if (highlighted[layer.id] === featureId) return;

          if (!isNil(highlighted[layer.id])) {
            map.unsetFeatureState({
              sourceId: getSourceId(layer.id),
              sourceLayer: layer.sourceLayer,
              featureId: highlighted[layer.id],
              featureStateFields: ['hover'],
            });
          }

          highlighted[layer.id] = featureId;

          map.setFeatureState({
            sourceId: getSourceId(layer.id),
            sourceLayer: layer.sourceLayer,
            featureId,
            featureState: {
              hover: true,
            },
          });
        };

        map._native.on('mousemove', layer.id, mouseMoveHandlers[layer.id]);

        mouseLeaveHandlers[layer.id] = () => {
          if (isNil(highlighted[layer.id])) return;

          map.unsetFeatureState({
            sourceId: getSourceId(layer.id),
            sourceLayer: layer.sourceLayer,
            featureId: highlighted[layer.id],
            featureStateFields: ['hover'],
          });

          delete highlighted[layer.id];
        };

        map._native.on('mouseleave', layer.id, mouseLeaveHandlers[layer.id]);
      }
    }

    return () => {
      for (const layerId in mouseMoveHandlers) {
        map._native.off('mousemove', layerId, mouseMoveHandlers[layerId]);
      }
      for (const layerId in mouseLeaveHandlers) {
        map._native.off('mouseleave', layerId, mouseLeaveHandlers[layerId]);
      }

      for (const [layerId, featureId] of Object.entries(highlighted)) {
        map.unsetFeatureState({
          sourceId: getSourceId(layerId),
          sourceLayer: sourceLayers[layerId],
          featureId,
          featureStateFields: ['hover'],
        });
      }
    };
  }, [map, shouldSkip, workspaceItems]);
}
